// src/pages/ResetPasswordPage.js
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DATA_SERVICE_BASE_URL } from "../../services/ApiService";
import "./ResetPasswordPage.css";

function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(
    () => new URLSearchParams(location.search).get("token") || "",
    [location.search]
  );

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const rules = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    // Must include at least one of: ! @ # $ % ^ & * ( )
    special: /[!@#$%^&*()]/.test(newPassword),
    match: newPassword.length > 0 && newPassword === confirmPassword,
  };
  const allValid = Object.values(rules).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");

    if (!token) {
      setServerError("Missing or invalid reset token.");
      return;
    }
    if (!allValid) {
      setServerError("Please meet all password requirements before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const url = `${DATA_SERVICE_BASE_URL}/api/users/reset-password?token=${encodeURIComponent(
        token
      )}&newPassword=${encodeURIComponent(newPassword)}`;
      const res = await fetch(url, { method: "POST", credentials: "include" });

      if (res.ok) {
        navigate("/login", { replace: true });
      } else {
        let message = "Failed to reset password. Please try again.";
        const ct = res.headers.get("content-type") || "";
        try {
          if (ct.includes("application/json")) {
            const data = await res.json();
            message = data?.message || data?.error || message;
          } else {
            const text = await res.text();
            message = text || message;
          }
        } catch (_) {}
        setServerError(message);
      }
    } catch (err) {
      setServerError(err?.message || "Unexpected error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="reset-password-card">
        <div className="reset-password-brand">
          <h1>DocuHounds</h1>
        </div>
        <h2>Set a New Password</h2>

        {!token && <p className="message">Missing reset token. Check your link.</p>}

        {serverError && (
          <div className="error-box" role="alert" aria-live="assertive">
            <div className="error-title">We couldn’t reset your password</div>
            <div className="error-details">{serverError}</div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
            required
            aria-describedby="password-rules"
          />
          <input
            type="password"
            placeholder="Enter again"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitting}
            required
          />

          <div id="password-rules" className="password-rules" aria-live="polite">
            <ul>
              <li className={rules.length ? "passed" : "failed"}>
                {rules.length ? "✓" : "✗"} At least 8 characters
              </li>
              <li className={rules.uppercase ? "passed" : "failed"}>
                {rules.uppercase ? "✓" : "✗"} Contains one capital letter
              </li>
              <li className={rules.lowercase ? "passed" : "failed"}>
                {rules.lowercase ? "✓" : "✗"} Contains one lowercase letter
              </li>
              <li className={rules.digit ? "passed" : "failed"}>
                {rules.digit ? "✓" : "✗"} Contains one digit (0–9)
              </li>
              <li className={rules.special ? "passed" : "failed"}>
                {rules.special ? "✓" : "✗"} Contains one special character (!@#$%^&*())
              </li>
              <li className={rules.match ? "passed" : "failed"}>
                {rules.match ? "✓" : "✗"} Passwords match
              </li>
            </ul>
          </div>

          <button type="submit" disabled={submitting || !token || !allValid}>
            {submitting ? "Submitting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
